import { useTranslation } from '@/hooks/use-translation';
import { motion } from 'motion/react';

export function Solution() {
  const { t, tArray } = useTranslation();
  const features = tArray<string>('solution.features');

  return (
    <section
      id="solution"
      className="bg-slate-50 dark:bg-[#0a0f14] py-20 lg:py-28 relative overflow-hidden"
    >
      {/* Subtle grid overlay */}
      <div className="absolute inset-0 opacity-10">
        <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern
              id="solution-grid"
              width="80"
              height="80"
              patternUnits="userSpaceOnUse"
            >
              <path
                d="M 80 0 L 0 0 0 80"
                fill="none"
                className="stroke-slate-400 dark:stroke-[#3d5a6e]"
                strokeWidth="0.5"
              />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#solution-grid)" />
        </svg>
      </div>

      <div className="max-w-6xl mx-auto px-6 relative z-10">
        {/* Header content */}
        <motion.div
          className="mb-16 text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="inline-block mb-4 text-[#3d6a7a] dark:text-[#7ab8c9] text-sm md:text-base tracking-[0.2em] uppercase font-medium">
            {t('solution.label')}
          </span>
          <h2 className="font-display text-slate-900 dark:text-[#e8ebe9] text-4xl md:text-5xl lg:text-6xl leading-tight mb-8 tracking-tight max-w-4xl mx-auto">
            {t('solution.title')}
            <br />
            {t('solution.titleLine2')}
          </h2>
          <p className="text-slate-600 dark:text-[#8a9a9e] text-lg md:text-xl leading-relaxed max-w-3xl mx-auto">
            {t('solution.description')}
          </p>
        </motion.div>

        {/* Feature list - horizontal on larger screens */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {features.map((item, index) => (
            <motion.div
              key={index}
              className="flex items-start gap-3 p-4 rounded-xl bg-white/50 dark:bg-[#0d1318]/50 border border-slate-200 dark:border-[#1a2530] backdrop-blur-sm"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.3 + index * 0.1 }}
            >
              <div className="w-1.5 h-1.5 mt-2 rounded-full bg-[#3d6a7a] dark:bg-[#7ab8c9] shrink-0" />
              <span className="text-slate-700 dark:text-[#c8d0d2] text-sm md:text-base">
                {item}
              </span>
            </motion.div>
          ))}
        </motion.div>

        {/* Full-width image */}
        <motion.div
          className="relative"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <div className="relative rounded-2xl overflow-hidden border border-slate-200 dark:border-[#1a2530] bg-slate-100 dark:bg-[#0d1318] shadow-2xl">
            {/* Main image */}
            <img
              src="/spatial-infrastructure-for-urban-sustainability.png"
              alt="Urban Maps spatial infrastructure visualization showing data aggregation from public registries, regulatory filings, sensor networks, and private sources into a unified sustainability dashboard"
              className="w-full h-auto"
            />

            {/* Gradient overlay for better integration */}
            <div className="absolute inset-0 bg-linear-to-t from-slate-900/10 via-transparent to-transparent pointer-events-none" />
          </div>

          {/* Corner accents */}
          <div className="absolute -top-3 -left-3 w-10 h-10 border-l-2 border-t-2 border-[#3d6a7a] dark:border-[#5a8a9a] rounded-tl-xl" />
          <div className="absolute -bottom-3 -right-3 w-10 h-10 border-r-2 border-b-2 border-[#3d6a7a] dark:border-[#5a8a9a] rounded-br-xl" />
        </motion.div>
      </div>
    </section>
  );
}
